/**
 * Sprint 11 — Admin Email Template Management (DB-backed via /api/v1/email-templates).
 *
 * Admin can: list, create, edit (subject + html + text + status), preview
 * (rendered with sample payload), and soft-delete. Templates are referenced
 * by code from the dispatch helper (`app/services/email_dispatch.py`).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { v1Get, v1Post, v1Patch, v1Delete } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Save, Plus, Eye, Mail, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import EmailHtmlEditor from '@/components/email/EmailHtmlEditor';
import EmailVariableHashInput from '@/components/email/EmailVariableHashInput';
import { sanitizeEmailHtml } from '@/lib/sanitizeEmailHtml';
import { EMAIL_HTML_SURFACE_CLASS } from '@/lib/emailHtmlSurface';
import { fromEditorHtml } from '@/lib/emailTemplateJinjaDom';
import {
  insertVariablesFor,
  sampleFor,
  variablesSchemaFromTemplate,
} from '@/lib/emailTemplateVars';
import { cn } from '@/lib/utils';

const BLANK_NEW_FORM = {
  code: '',
  name: '',
  description: '',
  subject_template: '',
  html_template: '<p>Hi {{ candidate_name }},</p>',
  text_template: '',
  status: 'active',
};

function isNewFormDirty(form) {
  return form.code !== BLANK_NEW_FORM.code
    || form.name !== BLANK_NEW_FORM.name
    || form.description !== BLANK_NEW_FORM.description
    || form.subject_template !== BLANK_NEW_FORM.subject_template
    || form.html_template !== BLANK_NEW_FORM.html_template
    || form.text_template !== BLANK_NEW_FORM.text_template
    || form.status !== BLANK_NEW_FORM.status;
}

/** Persist path: repair TipTap-mangled Jinja lists, then sanitize. */
function prepareHtmlForSave(html) {
  return sanitizeEmailHtml(fromEditorHtml(html));
}

function isDraftDirty(draft, selected) {
  if (!draft || !selected || draft.id !== selected.id) return false;
  return draft.name !== selected.name
    || (draft.description ?? '') !== (selected.description ?? '')
    || draft.subject_template !== selected.subject_template
    || prepareHtmlForSave(draft.html_template) !== prepareHtmlForSave(selected.html_template)
    || (draft.text_template ?? '') !== (selected.text_template ?? '')
    || draft.status !== selected.status;
}

export default function EmailTemplates() {
  const qc = useQueryClient();
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['v1-email-templates'],
    queryFn: () => v1Get('/email-templates?include_inactive=true'),
  });

  const [selectedId, setSelectedId] = useState(null);
  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId) || null,
    [templates, selectedId],
  );

  const [draft, setDraft] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const discardConfirmedRef = useRef(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPayload, setPreviewPayload] = useState('{}');
  const [previewResult, setPreviewResult] = useState(null);
  const [newForm, setNewForm] = useState({ ...BLANK_NEW_FORM });

  useEffect(() => {
    if (!selected && templates.length) setSelectedId(templates[0].id);
  }, [templates, selected]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (selected) setDraft({ ...selected }); }, [selected?.id]);

  const resetNewForm = () => setNewForm({ ...BLANK_NEW_FORM });

  const handleNewDialogOpenChange = (open) => {
    if (!open) {
      if (isNewFormDirty(newForm)) {
        setNewOpen(false);
        setDiscardConfirmOpen(true);
        return;
      }
      resetNewForm();
    }
    setNewOpen(open);
  };

  const handleDiscardConfirmOpenChange = (open) => {
    setDiscardConfirmOpen(open);
    if (!open) {
      if (discardConfirmedRef.current) {
        discardConfirmedRef.current = false;
        return;
      }
      if (isNewFormDirty(newForm)) {
        setNewOpen(true);
      }
    }
  };

  const confirmDiscardNewForm = () => {
    discardConfirmedRef.current = true;
    resetNewForm();
    setNewOpen(false);
  };

  const saveMut = useMutation({
    mutationFn: (body) => v1Patch(`/email-templates/${selected.id}`, body),
    onSuccess: () => { toast.success('Template saved'); qc.invalidateQueries({ queryKey: ['v1-email-templates'] }); },
    onError: (err) => toast.error(err?.response?.data?.detail?.message || 'Save failed'),
  });

  const createMut = useMutation({
    mutationFn: () => {
      const html_template = prepareHtmlForSave(newForm.html_template);
      return v1Post('/email-templates', {
        ...newForm,
        html_template,
        variables_schema_json: variablesSchemaFromTemplate({
          ...newForm,
          html_template,
        }),
      });
    },
    onSuccess: (created) => {
      toast.success('Template created');
      qc.invalidateQueries({ queryKey: ['v1-email-templates'] });
      setSelectedId(created.id);
      setDraft({ ...created });
      setNewOpen(false);
      resetNewForm();
    },
    onError: (err) => toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || 'Create failed'),
  });

  const deleteMut = useMutation({
    mutationFn: () => v1Delete(`/email-templates/${selected.id}`),
    onSuccess: () => {
      toast.success('Template deleted');
      qc.invalidateQueries({ queryKey: ['v1-email-templates'] });
      setSelectedId(null);
    },
  });

  const previewMut = useMutation({
    mutationFn: ({ payload }) => v1Post(`/email-templates/${selected.id}/preview`, { payload }),
    onSuccess: (res) => setPreviewResult(res),
    onError: (err) => toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || 'Render failed'),
  });

  const openPreview = () => {
    // Prefer draft content so sample vars match what the admin is editing;
    // the server still renders the last saved row for this id.
    const sample = sampleFor(draft || selected);
    setPreviewPayload(JSON.stringify(sample, null, 2));
    setPreviewResult(null);
    setPreviewOpen(true);
    previewMut.mutate({ payload: sample });
  };

  const runPreview = () => {
    let payload;
    try {
      payload = JSON.parse(previewPayload || '{}');
    } catch (e) {
      toast.error('Invalid JSON payload');
      return;
    }
    previewMut.mutate({ payload });
  };

  const draftDirty = useMemo(
    () => isDraftDirty(draft, selected),
    [draft, selected],
  );
  const draftVars = useMemo(
    () => (draft ? variablesSchemaFromTemplate(draft).required : []),
    [draft],
  );
  const draftInsertVars = useMemo(() => insertVariablesFor(draft), [draft]);
  const newInsertVars = useMemo(() => insertVariablesFor(newForm), [newForm]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Email Templates"
        subtitle="Admin-managed email templates. Type # to insert fields, or use Insert logic — no coding required."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline" className="gap-1.5"
              onClick={openPreview} disabled={!selected}
              data-testid="preview-email-button"
            >
              <Eye className="h-4 w-4" /> Preview
            </Button>
            <Button
              variant="outline" className="hidden gap-1.5"
              onClick={() => setNewOpen(true)}
              data-testid="new-email-template-button"
            >
              <Plus className="h-4 w-4" /> New template
            </Button>
            <Button
              className="gap-1.5"
              onClick={() => {
                const html_template = prepareHtmlForSave(draft.html_template);
                saveMut.mutate({
                  name: draft.name,
                  description: draft.description,
                  subject_template: draft.subject_template,
                  html_template,
                  text_template: draft.text_template,
                  status: draft.status,
                  variables_schema_json: variablesSchemaFromTemplate({
                    ...draft,
                    html_template,
                  }),
                });
              }}
              disabled={!draft || !draftDirty || saveMut.isPending}
              data-testid="save-email-button"
            >
              <Save className="h-4 w-4" /> Save
            </Button>
          </div>
        }
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
        <Card className="card-elevated">
          <CardContent className="p-3">
            <p className="px-2 pb-2 pt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Templates ({templates.length})
            </p>
            <ul className="space-y-1">
              {isLoading && <li className="px-3 py-2 text-xs text-muted-foreground">Loading…</li>}
              {templates.map((t) => (
                <li key={t.id}>
                  <button
                    onClick={() => { setSelectedId(t.id); setDraft({ ...t }); }}
                    data-testid="email-template-item"
                    className={`flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                      selectedId === t.id ? 'bg-primary/10 text-primary' : 'hover:bg-accent'
                    }`}
                  >
                    <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="truncate font-medium">{t.name}</p>
                      <p className="truncate text-[11px] text-muted-foreground font-mono">{t.code}</p>
                    </div>
                    {t.status !== 'active' && (
                      <Badge variant="outline" className="ml-auto text-[10px]">{t.status}</Badge>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Draft must match the selection before rendering: the rich editor reads
            its HTML once on mount, so a mismatched frame would strand another
            template's body in the editor. */}
        {selected && draft?.id === selected.id ? (
          <Card className="card-elevated">
            <CardContent className="space-y-4 p-6" key={selected.id}>
              <div className="flex items-center gap-3">
                <Badge variant="secondary" className="font-mono">{selected.code}</Badge>
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Active</span>
                  <Switch
                    checked={draft.status === 'active'}
                    onCheckedChange={(v) => setDraft({ ...draft, status: v ? 'active' : 'inactive' })}
                    data-testid="email-template-status-toggle"
                  />
                  <Button
                    variant="ghost" size="sm"
                    className="gap-1.5 text-rose-700 hover:bg-rose-50"
                    onClick={() => {
                      if (window.confirm(`Delete template "${selected.name}"?`)) deleteMut.mutate();
                    }}
                    data-testid="email-template-delete-button"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Display name</label>
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  data-testid="email-template-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Description</label>
                <Input
                  value={draft.description || ''}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Subject</label>
                <EmailVariableHashInput
                  value={draft.subject_template}
                  onChange={(subject_template) => setDraft({ ...draft, subject_template })}
                  variables={draftInsertVars}
                  placeholder="Type # to insert a dynamic variable…"
                  className="font-mono text-[13px]"
                  data-testid="email-template-subject-input"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Type # to insert merge fields (stored as {'{{ variable }}'}).
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">HTML body</label>
                <div className="mt-1 space-y-1.5">
                  <EmailHtmlEditor
                    key={selected.id}
                    value={draft.html_template}
                    onChange={(html) => setDraft({ ...draft, html_template: html })}
                    variables={draftInsertVars}
                    data-testid="email-template-html-editor"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Visual editor — type # for merge fields and use Insert logic
                    to show/hide content or list items (no coding required).
                  </p>
                </div>
              </div>
              <div className="hidden">
                <label className="text-xs font-medium text-muted-foreground">Plain-text body (optional)</label>
                <Textarea
                  rows={6}
                  value={draft.text_template || ''}
                  onChange={(e) => setDraft({ ...draft, text_template: e.target.value })}
                  className="font-mono text-[13px]"
                  data-testid="email-template-text-input"
                />
              </div>
              {draftVars.length > 0 && (
                <div className="rounded-xl border bg-secondary/30 p-3 text-xs text-muted-foreground">
                  <p className="font-semibold text-foreground">Variables in template</p>
                  <p className="mt-1 font-mono">
                    {draftVars.map((k) => `{{ ${k} }}`).join(' · ')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="card-elevated">
            <CardContent className="p-10 text-center text-sm text-muted-foreground">
              {isLoading ? 'Loading templates…' : 'Select a template to edit.'}
            </CardContent>
          </Card>
        )}
      </div>

      {/* New template dialog */}
      <Dialog open={newOpen} onOpenChange={handleNewDialogOpenChange}>
        <DialogContent className="sm:max-w-3xl" data-testid="new-email-template-dialog">
          <DialogHeader>
            <DialogTitle>New email template</DialogTitle>
            <DialogDescription>
              Code must be lowercase, snake_case, and unique. Use Jinja2 syntax (e.g. <code>{'{{ candidate_name }}'}</code>).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Code</label>
              <Input
                value={newForm.code}
                onChange={(e) => setNewForm({ ...newForm, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                placeholder="e.g. candidate_reminder"
                className="font-mono"
                data-testid="new-template-code-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Display name</label>
              <Input
                value={newForm.name}
                onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                placeholder="e.g. Candidate panel reminder"
                data-testid="new-template-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Subject</label>
              <EmailVariableHashInput
                value={newForm.subject_template}
                onChange={(subject_template) => setNewForm({ ...newForm, subject_template })}
                variables={newInsertVars}
                placeholder="Type # to insert a dynamic variable…"
                className="font-mono text-[13px]"
                data-testid="new-template-subject-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">HTML body</label>
              <div className="mt-1 space-y-1.5">
                {newOpen && (
                  <EmailHtmlEditor
                    key="new-email-template-editor"
                    value={newForm.html_template}
                    onChange={(html) => setNewForm((prev) => ({ ...prev, html_template: html }))}
                    variables={newInsertVars}
                    data-testid="new-template-html-editor"
                  />
                )}
                <p className="text-[11px] text-muted-foreground">
                  Type # for merge fields and use Insert logic to build show/hide
                  or list blocks. HireIQ writes the template syntax for you.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => handleNewDialogOpenChange(false)}
              data-testid="new-email-template-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => createMut.mutate()}
              disabled={!newForm.code || !newForm.name || !newForm.subject_template || !newForm.html_template || createMut.isPending}
              data-testid="new-email-template-confirm"
            >
              {createMut.isPending ? 'Creating…' : 'Create template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={discardConfirmOpen} onOpenChange={handleDiscardConfirmOpenChange}>
        <AlertDialogContent data-testid="new-email-template-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>You have unsaved changes. Are you sure you want to discard them?</AlertDialogTitle>
            <AlertDialogDescription>
              Your entered template data will be lost if you discard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="new-email-template-continue-editing">Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscardNewForm} data-testid="new-email-template-discard-confirm">
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-3xl" data-testid="preview-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" /> Email preview
            </DialogTitle>
            <DialogDescription>
              Renders the template with the sample payload below using the same Jinja2 engine the server uses.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Sample payload (JSON)</label>
              <Textarea
                rows={14}
                value={previewPayload}
                onChange={(e) => setPreviewPayload(e.target.value)}
                className="font-mono text-[12px]"
                data-testid="preview-payload-input"
              />
              <Button
                variant="outline" className="mt-2 w-full gap-1.5"
                onClick={runPreview} disabled={previewMut.isPending}
                data-testid="preview-render-button"
              >
                <Eye className="h-3.5 w-3.5" /> {previewMut.isPending ? 'Rendering…' : 'Re-render'}
              </Button>
            </div>
            <div className="overflow-hidden rounded-xl border bg-white">
              <div className="border-b bg-secondary/40 px-4 py-3 text-xs text-muted-foreground">
                <p className="font-semibold text-foreground text-[13px]">
                  {previewResult?.subject || <span className="text-muted-foreground">(subject will appear here)</span>}
                </p>
              </div>
              <div
                className={cn(
                  'max-h-[400px] overflow-auto px-5 py-5',
                  // Same surface as the editor — Preflight hides ul/ol markers otherwise.
                  EMAIL_HTML_SURFACE_CLASS,
                )}
                data-testid="preview-html-output"
                dangerouslySetInnerHTML={{ __html: previewResult?.html || '<p style="color:#888">(rendered HTML will appear here)</p>' }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
